import { Link, useLocation } from 'react-router-dom';
import '../styles/homeTheme.css';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { CONSULTATION_15MIN_URL } from '../lib/links';

/**
 * Branded 404 page, themed with the light "command center" theme.
 * Catches:
 *   1. Any path that isn't a known SPA route (via the wildcard route
 *      in App.tsx).
 *   2. Cold loads of unknown paths (vercel.json now rewrites every
 *      unrecognized path to /index.html so the SPA can render this
 *      page).
 *
 * Search engines should still receive a 404 status for these URLs.
 * Vercel's SPA-rewrite serves 200 OK on the rewritten /index.html by
 * default, so we emit `<Seo robots="noindex, nofollow">` to keep these
 * pages out of the index even when Google can't see a literal 404 status.
 */
export const NotFoundPage = () => {
  const location = useLocation();

  return (
    <>
      <div className="ht-root nf-page">
        <Seo
          title="Page Not Found · BRANDCENTRAL"
          description="The page you're looking for doesn't exist. Head back to BRANDCENTRAL home or pick from the most-used links below."
          path={location.pathname}
          robots="noindex, nofollow"
        />

        <div className="nf-inner">
          <p className="aa-eyebrow">Error 404</p>
          <h1 className="aa-h1" style={{ maxWidth: 'none' }}>
            Not <span className="aa-grad">here.</span>
          </h1>
          <p className="aa-intro" style={{ margin: '26px auto 0' }}>
            The page at{' '}
            <code className="nf-code">{location.pathname}</code>{' '}
            doesn&rsquo;t exist on this site. It either moved, was never here,
            or you followed a stale link.
          </p>

          <div className="nf-chips">
            {[
              { label: 'Home', href: '/' },
              { label: 'AI Agency', href: '/ai-agency' },
              { label: 'Studio', href: '/studio' },
              { label: 'About', href: '/about' },
              { label: 'Blog', href: '/blog' },
              { label: 'Tools', href: '/tools' },
            ].map((item) => (
              <Link key={item.label} to={item.href} className="tk-fpill">
                {item.label}
              </Link>
            ))}
          </div>

          <div className="nf-actions">
            <a className="ht-btn ht-btn-hero" href={CONSULTATION_15MIN_URL} target="_blank" rel="noopener noreferrer">
              Book a free 15-min call →
            </a>
            <Link className="ht-btn ht-btn-hero ht-btn-light" to="/">
              Back to home →
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
